import React, {useState} from 'react'
import {Button} from 'react-daisyui'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCheckCircle, faClose} from '@fortawesome/free-solid-svg-icons'
import {
  useApplyModifyReservationMutation,
  useQuoteModifyReservationMutation
} from '../../services/guestPortalApi'
import DatePickerInput from '../common/DatePickerInput'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {
  trackBookingManagement,
  trackFormSubmission
} from '../../services/analytics'
import {trackClick} from '../../services/coreAnalytics'
import {getUser, getUserToken} from '../../services/token'
import {formatDateString} from '../../services/utils'
import {
  applyErrorMessageFor,
  confirmationTotal,
  deltaSummary,
  formatMoney,
  ineligibleMessageFor,
  ModifyQuoteResponse,
  toIsoDate
} from './modifyReservationHelpers'

interface ModifyReservationFormProps {
  reservation: any
  onDone?: () => void
  onGoBack?: () => void
}

const userEmail = (): string | undefined => {
  const token = getUserToken()
  return token ? getUser(token)?.email : undefined
}

const ModifyReservationForm: React.FC<ModifyReservationFormProps> = ({
  reservation,
  onDone,
  onGoBack
}) => {
  const [quoteModify, quoteResult] = useQuoteModifyReservationMutation()
  const [applyModify, applyResult] = useApplyModifyReservationMutation()

  const [arrival, setArrival] = useState<Date | undefined>(undefined)
  const [departure, setDeparture] = useState<Date | undefined>(undefined)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)

  const quote: ModifyQuoteResponse | undefined = quoteResult.data
  const analyticsPage = `/member/stays/${reservation.reservationId}`

  const requestQuote = () => {
    if (!arrival || !departure) {
      return
    }
    const email = userEmail()
    trackClick('get-modify-quote-button', analyticsPage, email)
    trackFormSubmission('booking_modify', 'form_submitted', undefined, email)
    setApplyError(null)
    quoteModify({
      reservationId: reservation.reservationId,
      arrival: toIsoDate(arrival),
      departure: toIsoDate(departure)
    })
  }

  const confirmChange = () => {
    if (!quote?.eligible || !arrival || !departure) {
      return
    }
    const email = userEmail()
    trackClick('confirm-modify-reservation-button', analyticsPage, email)
    applyModify({
      reservationId: reservation.reservationId,
      arrival: toIsoDate(arrival),
      departure: toIsoDate(departure),
      expectedNewTotal: confirmationTotal(quote)
    })
      .unwrap()
      .then(() => {
        trackBookingManagement(
          'modify_booking_completed',
          reservation.reservationId,
          undefined,
          email
        )
        trackFormSubmission('booking_modify', 'form_success', undefined, email)
        setShowSuccess(true)
      })
      .catch(error => {
        trackBookingManagement(
          'modify_booking_failed',
          reservation.reservationId,
          'API Error',
          email
        )
        trackFormSubmission(
          'booking_modify',
          'form_error',
          ['Unable to modify reservation'],
          email
        )
        setApplyError(applyErrorMessageFor(error))
        // Rates moved between preview and confirm — refresh the quote so
        // the guest confirms against the current price
        if (error?.data?.error === 'quote-changed') {
          quoteModify({
            reservationId: reservation.reservationId,
            arrival: toIsoDate(arrival),
            departure: toIsoDate(departure)
          })
        }
      })
  }

  const isBusy = quoteResult.isLoading || applyResult.isLoading

  if (showSuccess) {
    const outcome = applyResult.data?.outcome
    return (
      <div>
        <div className="absolute top-0 right-0">
          <button
            className="btn-close"
            aria-label="Close date change confirmation"
            onClick={() => onDone?.()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                if (onDone) {
                  onDone()
                }
              }
            }}
          >
            <FontAwesomeIcon
              icon={faClose}
              className="text-xl fa-thin text-center"
            />
          </button>
        </div>
        <div className="text-green-500 flex flex-col items-center p-4">
          <FontAwesomeIcon icon={faCheckCircle} className="text-5xl p-4" />
        </div>
        <div className="text-center flex flex-col gap-2">
          <p className="font-semibold">Done. Your dates are updated.</p>
          {outcome?.newReservationId && (
            <p>
              Your added nights are a separate reservation — you’ll get its own
              confirmation shortly.
            </p>
          )}
          {outcome?.adjustmentPending && (
            <p>
              Your confirmed total is {formatMoney(confirmationTotal(quote))}.
              Our team is finishing the folio update — it’ll reflect shortly.
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      aria-labelledby="modify-reservation-title"
      onKeyDown={e => e.key === 'Escape' && onGoBack?.()}
    >
      <h1
        id="modify-reservation-title"
        className="font-bold text-2xl mb-2"
        role="heading"
        aria-level={1}
      >
        Change Your Dates
      </h1>
      <p className="mb-4">
        Currently {formatDateString(reservation.checkinDate)} –{' '}
        {formatDateString(reservation.checkoutDate)}. Pick your new dates and
        we’ll show the updated price before anything changes.
      </p>

      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex-1">
          <label className="block mb-1 font-medium">New check-in</label>
          <DatePickerInput
            value={arrival}
            onChange={setArrival}
            placeholder="MM/DD/YYYY"
          />
        </div>
        <div className="flex-1">
          <label className="block mb-1 font-medium">New check-out</label>
          <DatePickerInput
            value={departure}
            onChange={setDeparture}
            placeholder="MM/DD/YYYY"
          />
        </div>
      </div>

      {isBusy && (
        <div
          className="flex flex-row justify-center items-center py-4 gap-2"
          role="status"
          aria-live="polite"
        >
          <LoadingSpinner size={LoadingSpinnerSize.Large} />
          <span>Checking availability…</span>
        </div>
      )}

      {quoteResult.isError && !isBusy && (
        <div className="text-red-500 mb-4" role="alert" aria-live="assertive">
          We couldn’t price those dates. Try again or contact the property.
        </div>
      )}

      {quote && !quote.eligible && !isBusy && (
        <div className="bg-base-200 rounded-lg p-4 mb-4" role="alert">
          {ineligibleMessageFor(quote.reason)}
        </div>
      )}

      {quote?.eligible && !isBusy && (
        <div className="bg-base-200 rounded-lg p-4 mb-4">
          <div className="flex justify-between mb-1">
            <span>Current total</span>
            <span>{formatMoney(quote.currentTotal)}</span>
          </div>
          <div className="flex justify-between font-semibold mb-2">
            <span>
              {quote.newReservationTotal ? 'Added nights' : 'New total'}
            </span>
            <span>{formatMoney(confirmationTotal(quote))}</span>
          </div>
          <p className="text-sm">{deltaSummary(quote)}</p>
          {Boolean(quote.detail?.sameDayNightCharged) && (
            <p className="text-sm mt-1">
              Since it’s after 11 AM, tonight is still part of your stay.
            </p>
          )}
        </div>
      )}

      {applyError && !isBusy && (
        <div className="text-red-500 mb-4" role="alert" aria-live="assertive">
          {applyError}
        </div>
      )}

      <div className="flex justify-end p-2 gap-2">
        <Button
          className="btn-md btn-outline btn-secondary"
          onClick={() => {
            trackFormSubmission(
              'booking_modify',
              'form_cancelled',
              undefined,
              userEmail()
            )
            if (onGoBack) {
              onGoBack()
            }
          }}
          aria-label="Go back without changing dates"
        >
          go back
        </Button>
        {!quote?.eligible && (
          <Button
            className="btn btn-md btn-secondary"
            disabled={!arrival || !departure || isBusy}
            onClick={requestQuote}
            aria-label="See the updated price for the new dates"
          >
            see new price
          </Button>
        )}
        {quote?.eligible && (
          <>
            <Button
              className="btn-md btn-outline btn-secondary"
              disabled={isBusy}
              onClick={requestQuote}
              aria-label="Reprice after changing the dates again"
            >
              update price
            </Button>
            <Button
              className="btn btn-md btn-secondary"
              disabled={isBusy}
              onClick={confirmChange}
              aria-label="Confirm the date change at the shown price"
            >
              confirm change
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default ModifyReservationForm
